import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import dayjs from 'utils/dayjs';
import { FormattedMessage, useIntl } from 'react-intl';

import { ChartLayout } from 'components/ChartLayout';
import Spinner from 'components/Spinner';
import {
  CURRENCY_YEAR_ARCHIVE_SOURCE,
  CURRENCY_YEAR_ARCHIVE_SOURCE_FILTER_OPTIONS,
} from 'constants/currencyYearArchive';
import { colorPrimary, colorSecondary } from 'styles';
import {
  formatCurrencyYearArchiveChartMonthLabel,
  formatCurrencyYearArchiveChartTooltipDate,
} from 'utils/currencyYearArchive';
import { externalTooltipHandler, generateDatasets } from 'utils';

import {
  ChartCanvasSlot,
  ChartWrapper,
  Empty,
  HeaderRow,
  Legend,
  LegendDot,
  LegendItem,
  LegendMainGroup,
  LoadingOverlay,
  LoadingText,
  NbuLegendItem,
  NbuLegendLabel,
  NbuToggle,
  NbuToggleWrapper,
  Title,
  Wrapper,
} from './styles';

const NBU_LEGEND_COLOR = '#5E548E';
const YEAR_CHART_MONTH_TICKS = 12;
const YEAR_CHART_Y_TICKS = 7;
const CHART_MOBILE_MAX = 420;
const YEAR_CHART_MONTH_TICKS_MOBILE = 5;
const MOBILE_X_AXIS_MONTHS = [1, 4, 7, 10, 12];

const buildChartTerms = ({ isNbuOnly, isNbuOverlayVisible }) => {
  if (isNbuOnly) {
    return [{ type: 'nbu', value: 'nbu' }];
  }

  const terms = [
    { type: 'sell', value: 'primary' },
    { type: 'buy', value: 'primary' },
  ];

  if (isNbuOverlayVisible) {
    terms.splice(1, 0, { type: 'nbu', value: 'nbu' });
  }

  return terms;
};

const buildXTicks = isMobile => ({
  autoSkip: false,
  maxTicksLimit: isMobile
    ? YEAR_CHART_MONTH_TICKS_MOBILE
    : YEAR_CHART_MONTH_TICKS,
  maxRotation: 0,
  minRotation: 0,
  labelOffset: 1,
  font: {
    size: 13,
  },
  color: 'black',
});

const shouldShowYearChartMonthLabel = (parsed, isMobile) => {
  if (parsed.date() !== 1) {
    return false;
  }

  if (!isMobile) {
    return true;
  }

  return MOBILE_X_AXIS_MONTHS.includes(parsed.month() + 1);
};

const getMonthStartIndices = items =>
  items.reduce((indices, item, index) => {
    const day = Number(item.date?.slice(8, 10));

    if (day === 1) {
      indices.push(index);
    }

    return indices;
  }, []);

const getDownsampledIndices = (items, maxPoints) => {
  if (items.length <= maxPoints) {
    return items.map((_, index) => index);
  }

  const lastIndex = items.length - 1;
  const pinnedIndices = new Set([0, lastIndex, ...getMonthStartIndices(items)]);
  const selectedIndices = [...pinnedIndices].sort((a, b) => a - b);
  const remainingSlots = maxPoints - selectedIndices.length;

  if (remainingSlots > 0) {
    const availableIndices = [];

    for (let index = 0; index <= lastIndex; index += 1) {
      if (!pinnedIndices.has(index)) {
        availableIndices.push(index);
      }
    }

    if (availableIndices.length > 0) {
      const step = availableIndices.length / remainingSlots;

      for (let index = 0; index < remainingSlots; index += 1) {
        const sourceIndex =
          index === remainingSlots - 1
            ? availableIndices[availableIndices.length - 1]
            : availableIndices[Math.round(index * step)];

        selectedIndices.push(sourceIndex);
      }
    }
  }

  return [...new Set(selectedIndices)]
    .sort((a, b) => a - b)
    .slice(0, maxPoints);
};

const downsampleItems = (items, maxPoints) => {
  const indices = getDownsampledIndices(items, maxPoints);

  return indices.map(index => items[index]);
};

const downsampleAlignedItems = (primaryItems, nbuItems, maxPoints) => {
  const indices = getDownsampledIndices(primaryItems, maxPoints);

  return {
    primary: indices.map(index => primaryItems[index]),
    nbu: indices.map(index => nbuItems[index]),
  };
};

const buildChartOptions = (
  formatAxisRate,
  formatTooltipRate,
  days,
  locale,
  chartWidth
) => {
  const isMobile = chartWidth < CHART_MOBILE_MAX;

  return {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      autoPadding: false,
      padding: {
        bottom: 0,
      },
    },
    events: ['click', 'mousemove'],
    plugins: {
      crosshair: {
        line: {
          color: '#A8B0AC',
          width: 1,
        },
        zoom: {
          enabled: false,
        },
      },
      legend: {
        display: false,
      },
      tooltip: {
        usePointStyle: true,
        enabled: false,
        external: context => externalTooltipHandler(context, 'exchanger'),
        callbacks: {
          title(tooltipItems) {
            const index = tooltipItems[0]?.dataIndex;
            const date = days[index]?.date;

            if (!date) {
              return '';
            }

            return formatCurrencyYearArchiveChartTooltipDate(date, locale);
          },
          label(context) {
            return `${context.dataset.label}: ${formatTooltipRate(
              context.parsed.y
            )}`;
          },
        },
      },
    },
    interaction: {
      mode: 'index',
      intersect: false,
    },
    elements: {
      line: {
        tension: 0.1,
      },
      point: {
        radius: 0,
        hoverBackgroundColor: '#fff',
        hoverBorderWidth: 3,
        hoverRadius: 6,
      },
    },
    scales: {
      x: {
        offset: false,
        grid: {
          display: false,
        },
        ticks: {
          ...buildXTicks(isMobile),
          callback(value, index) {
            const date = days[index]?.date;

            if (!date) {
              return '';
            }

            const parsed = dayjs(date, 'YYYY-MM-DD', true);

            if (
              !parsed.isValid() ||
              !shouldShowYearChartMonthLabel(parsed, isMobile)
            ) {
              return '';
            }

            return formatCurrencyYearArchiveChartMonthLabel(date, locale);
          },
        },
      },
      y: {
        position: 'right',
        ticks: {
          count: YEAR_CHART_Y_TICKS,
          beginAtZero: false,
          callback: yValue => formatAxisRate(yValue),
          color: 'black',
          font: {
            size: 13,
          },
        },
        grid: {
          borderDash: [5, 5],
          drawBorder: false,
        },
      },
    },
  };
};

const flattenSourceDays = source =>
  (source?.months || []).reduce(
    (result, month) => result.concat(month.days || []),
    []
  );

const CurrencyYearArchiveChart = ({
  sourceType,
  primarySource,
  nbuSource,
  isLoading,
}) => {
  const { formatMessage, formatNumber, locale } = useIntl();
  const chartContainerRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(0);
  const [isNbuVisible, setIsNbuVisible] = useState(true);

  const [clientRender, setClientRender] = useState(false);
  const [isChartReady, setIsChartReady] = useState(false);

  const isNbuOnly = sourceType === CURRENCY_YEAR_ARCHIVE_SOURCE.nbu;
  const canShowNbuOverlay = sourceType === CURRENCY_YEAR_ARCHIVE_SOURCE.cash;

  const formatAxisRate = useMemo(
    () => value =>
      formatNumber(Number(value), {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    [formatNumber]
  );

  const formatTooltipRate = useMemo(
    () => value =>
      formatNumber(Number(value), {
        minimumFractionDigits: 4,
        maximumFractionDigits: 4,
      }),
    [formatNumber]
  );

  const primaryDays = useMemo(() => flattenSourceDays(primarySource), [
    primarySource,
  ]);
  const nbuDays = useMemo(() => flattenSourceDays(nbuSource), [nbuSource]);

  const getMaxYearChartPoints = useCallback(width => {
    // Mobile: fewer points to avoid over-dense "zigzag" rendering.
    if (!width) {
      return 48;
    }

    return width < CHART_MOBILE_MAX ? 36 : 48;
  }, []);

  const maxYearChartPoints = useMemo(() => getMaxYearChartPoints(chartWidth), [
    chartWidth,
    getMaxYearChartPoints,
  ]);

  const alignedDaysLength = useMemo(() => {
    if (!canShowNbuOverlay || !primaryDays.length || !nbuDays.length) {
      return primaryDays.length;
    }

    return Math.min(primaryDays.length, nbuDays.length);
  }, [canShowNbuOverlay, primaryDays.length, nbuDays.length]);

  const primaryDaysAligned = useMemo(
    () => primaryDays.slice(0, alignedDaysLength),
    [primaryDays, alignedDaysLength]
  );
  const nbuDaysAligned = useMemo(() => nbuDays.slice(0, alignedDaysLength), [
    nbuDays,
    alignedDaysLength,
  ]);

  const { primary: days, nbu: nbuChartDays } = useMemo(() => {
    if (!primaryDaysAligned.length) {
      return { primary: [], nbu: [] };
    }

    if (!canShowNbuOverlay || !nbuDaysAligned.length) {
      return {
        primary: downsampleItems(primaryDaysAligned, maxYearChartPoints),
        nbu: [],
      };
    }

    return downsampleAlignedItems(
      primaryDaysAligned,
      nbuDaysAligned,
      maxYearChartPoints
    );
  }, [
    primaryDaysAligned,
    nbuDaysAligned,
    maxYearChartPoints,
    canShowNbuOverlay,
  ]);

  useEffect(() => {
    if (!days.length) {
      return undefined;
    }

    const node = chartContainerRef.current;

    if (!node) {
      return undefined;
    }

    const updateWidth = () => {
      setChartWidth(node.clientWidth);
    };

    updateWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateWidth);

      return () => window.removeEventListener('resize', updateWidth);
    }

    const observer = new ResizeObserver(updateWidth);
    observer.observe(node);

    return () => observer.disconnect();
  }, [days.length]);

  const toggleNbu = useCallback(() => {
    setIsNbuVisible(current => !current);
  }, []);

  useEffect(() => {
    setClientRender(true);
  }, []);

  const handleChartReady = useCallback(() => {
    setIsChartReady(true);
  }, []);

  const chartState = useMemo(() => {
    if (!days.length) {
      return null;
    }

    const labels = days.map(day => day.date);
    const isNbuOverlayVisible =
      canShowNbuOverlay && isNbuVisible && nbuChartDays.length > 0;
    const chartTerms = buildChartTerms({
      isNbuOnly,
      isNbuOverlayVisible,
    });

    const termsWithLocale = chartTerms.map(term => {
      if (term.type === 'nbu') {
        return {
          ...term,
          label: formatMessage({ id: 'currencyYearArchivePage.chart.nbu' }),
        };
      }

      return {
        ...term,
        label: formatMessage({ id: `interbank.${term.type}` }),
      };
    });

    const matrix = isNbuOnly
      ? {
          nbu: {
            rate: days.map(day => day.rate),
          },
        }
      : {
          primary: {
            sell: days.map(day => day.sell),
            buy: days.map(day => day.buy),
          },
          nbu: {
            rate: nbuChartDays.map(day => day.rate),
          },
        };

    const nbuLabel = formatMessage({ id: 'currencyYearArchivePage.chart.nbu' });
    const datasets = generateDatasets(
      termsWithLocale,
      matrix,
      isNbuOnly ? 'nbu' : 'exchanger'
    ).map(dataset =>
      dataset.label === nbuLabel
        ? { ...dataset, borderColor: [NBU_LEGEND_COLOR] }
        : dataset
    );

    return {
      labels,
      datasets,
      chartId: `currency-year-archive-${sourceType}-${
        isNbuOverlayVisible ? 'nbu-on' : 'nbu-off'
      }`,
      options: buildChartOptions(
        formatAxisRate,
        formatTooltipRate,
        days,
        locale,
        chartWidth
      ),
    };
  }, [
    days,
    nbuChartDays,
    isNbuVisible,
    isNbuOnly,
    canShowNbuOverlay,
    sourceType,
    locale,
    formatMessage,
    chartWidth,
    formatAxisRate,
    formatTooltipRate,
  ]);

  const { labels, datasets, chartId, options } = chartState || {};
  const hasNbuOverlay =
    canShowNbuOverlay && (isLoading || nbuChartDays.length > 0);
  const showLegend = Boolean(chartState) || isLoading || !clientRender;

  useEffect(() => {
    setIsChartReady(false);
  }, [chartId]);

  const showChart = clientRender && Boolean(chartState);
  const showLoading =
    !clientRender || isLoading || (showChart && !isChartReady);

  let chartContent = (
    <Empty>
      <FormattedMessage id="empty" />
    </Empty>
  );

  if (showChart || showLoading) {
    chartContent = (
      <ChartWrapper ref={chartContainerRef}>
        {showChart ? (
          <ChartCanvasSlot>
            <ChartLayout
              key={chartId}
              id={chartId}
              type="line"
              labels={labels}
              datasets={datasets}
              options={options}
              onReady={handleChartReady}
            />
          </ChartCanvasSlot>
        ) : null}
        {showLoading ? (
          <LoadingOverlay role="status" aria-live="polite">
            <Spinner />
            <LoadingText>
              <FormattedMessage id="currencyYearArchivePage.chart.loading" />
            </LoadingText>
          </LoadingOverlay>
        ) : null}
      </ChartWrapper>
    );
  }

  return (
    <Wrapper>
      <HeaderRow>
        <Title>
          <FormattedMessage id="currencyYearArchivePage.chart.title" />
        </Title>
        {showLegend ? (
          <Legend>
            {isNbuOnly ? (
              <LegendMainGroup>
                <LegendItem>
                  <LegendDot $color={NBU_LEGEND_COLOR} />
                  <FormattedMessage id="currencyYearArchivePage.chart.nbu" />
                </LegendItem>
              </LegendMainGroup>
            ) : (
              <>
                <LegendMainGroup>
                  <LegendItem>
                    <LegendDot $color={colorPrimary} />
                    <FormattedMessage id="interbank.buy" />
                  </LegendItem>
                  <LegendItem>
                    <LegendDot $color={colorSecondary} />
                    <FormattedMessage id="interbank.sell" />
                  </LegendItem>
                </LegendMainGroup>
                {hasNbuOverlay ? (
                  <NbuLegendItem>
                    <NbuLegendLabel>
                      <LegendDot $color={NBU_LEGEND_COLOR} />
                      <FormattedMessage id="currencyYearArchivePage.chart.nbu" />
                    </NbuLegendLabel>
                    <NbuToggleWrapper>
                      <NbuToggle
                        type="button"
                        $isActive={isNbuVisible}
                        aria-pressed={isNbuVisible}
                        aria-label={formatMessage({
                          id: 'currencyYearArchivePage.chart.nbu',
                        })}
                        onClick={toggleNbu}
                      />
                    </NbuToggleWrapper>
                  </NbuLegendItem>
                ) : null}
              </>
            )}
          </Legend>
        ) : null}
      </HeaderRow>
      {chartContent}
    </Wrapper>
  );
};

const sourceShape = PropTypes.shape({
  months: PropTypes.arrayOf(
    PropTypes.shape({
      days: PropTypes.arrayOf(PropTypes.shape({})),
    })
  ),
});

CurrencyYearArchiveChart.propTypes = {
  sourceType: PropTypes.oneOf(CURRENCY_YEAR_ARCHIVE_SOURCE_FILTER_OPTIONS)
    .isRequired,
  primarySource: sourceShape,
  nbuSource: sourceShape,
  isLoading: PropTypes.bool,
};

CurrencyYearArchiveChart.defaultProps = {
  primarySource: null,
  nbuSource: null,
  isLoading: false,
};

export default memo(CurrencyYearArchiveChart);
